import classNames from 'classnames';
import { describeItemAffix, resolveEssenceShortfall, type EssencePool } from '@sorrem/shared';
import { formatEssenceAmount } from '@sorrem-client/game/essence-integration';
import { EssenceOrb, Panel } from '@sorrem-client/ui/components';
import type { CraftingWorkbenchState } from '@sorrem-client/ui/features/CharacterHome/hooks';
import { capitalize } from '@sorrem-client/ui/shared/text';
import { describeShortfall, findUpgradeTarget, REROLL_COST_GROWTH_WORDS, resolveUpgradePath, resolveVesselRows, type CraftingMode, type VesselRow } from '../../crafting-preview';
import { useCraftingItem } from '../../use-crafting-item';
import styles from './CraftingVesselPanel.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface CraftingVesselPanelProps {
    readonly workbench: CraftingWorkbenchState;
}

/** What the vessel can show the cost of: the upgrade, or a reroll looked at on the workbench. */
interface VesselPreview {
    readonly mode: CraftingMode;
    readonly label: string;
    readonly cost: Partial<EssencePool>;
    /** Said after what it leaves the vessel with. */
    readonly afterNote: string;
}

/** The types an action spends, as what it leaves: "100 Resonance and 2 Fury". */
function describeLeftover(rows: readonly VesselRow[]): string {
    const parts = rows.filter((row) => row.spent > 0).map((row) => formatEssenceAmount(row.type, row.after ?? 0));
    return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * Crafting's right panel (#287): the vessel, each essence type now and
 * after the upgrade to the level chosen on the workbench, or, while a
 * reroll is looked at there, after the reroll. The actions themselves are
 * on the workbench. Banked essence is what crafting spends, and otherwise
 * shows only in the loadout's vessel.
 */
export function CraftingVesselPanel({ workbench }: CraftingVesselPanelProps): JSX.Element | null {
    const { character, item, preview } = useCraftingItem();

    if (character === null) {
        return null;
    }

    const banked = character.permanentEssence;
    const upgrade = ((): VesselPreview | null => {
        if (item === null || preview === null || preview.isNamed) {
            return null;
        }
        // An upgrade goes to the level chosen on the upgrade path, paying every level on the way.
        const targetLevel = workbench.resolveTargetLevel(item.id, preview.upgradeLevel);
        const upgradeTotal = findUpgradeTarget(resolveUpgradePath(item), targetLevel)?.total ?? null;
        return upgradeTotal === null ? null : {
            mode: 'upgrade',
            label: `Upgrade to +${targetLevel}`,
            cost: upgradeTotal,
            afterNote: 'The next upgrade costs more.',
        };
    })();
    const reroll = ((): VesselPreview | null => {
        if (item === null || preview === null || preview.isNamed || preview.rerollCost === undefined) {
            return null;
        }
        const affixes = item.instance?.affixes ?? [];
        const affix = affixes[workbench.resolvePreviewedReroll(item.id, affixes.length) ?? -1];
        return affix === undefined ? null : {
            mode: 'reroll',
            label: `Reroll ${describeItemAffix(affix)}`,
            cost: preview.rerollCost,
            afterNote: `The next reroll costs ${REROLL_COST_GROWTH_WORDS} as much.`,
        };
    })();

    const previewed = reroll ?? upgrade;
    // A reroll looked at lights up the rows it spends, as it takes over from the upgrade.
    const isHighlighted = reroll !== null;
    const rows = resolveVesselRows(banked, previewed?.cost ?? null);
    const previewedShortfall = previewed === null ? null : describeShortfall(resolveEssenceShortfall(banked, previewed.cost));

    return (
        <Panel className={styles.craftingVesselPanel}>
            <div className={styles.title} {...toTourTargetProps('crafting-vessel')}>
                <h3 className={styles.heading}>Your vessel</h3>
                <p className={styles.note}>Banked essence, what crafting spends.</p>
            </div>
            <div className={styles.vessel}>
                <table className={styles.rows}>
                    <thead>
                        <tr>
                            <th scope='col'>Essence</th>
                            <th scope='col' className={styles.amount}>Now</th>
                            <th scope='col' aria-label='Becomes' />
                            <th scope='col' className={styles.amount}>{previewed === null ? '' : 'After'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.type}
                                className={classNames(
                                    row.spent === 0 && styles.unspent,
                                    row.short > 0 && styles.short,
                                    isHighlighted && row.spent > 0 && styles.highlighted,
                                )}
                            >
                                <th scope='row' className={styles.type}>
                                    <EssenceOrb type={row.type} className={styles.orb} />
                                    {capitalize(row.type)}
                                </th>
                                <td className={styles.amount}>{row.held.toLocaleString()}</td>
                                <td className={styles.becomes} aria-hidden='true'>{row.spent > 0 ? '->' : ''}</td>
                                <td className={classNames(styles.amount, styles.after)}>
                                    {previewed === null ? '' : row.after === null ? `short ${row.short.toLocaleString()}` : row.after.toLocaleString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {previewed !== null && (
                    <div className={classNames(styles.summary, previewedShortfall !== null && styles.isShort)}>
                        {previewedShortfall === null ? (
                            <>
                                <span className={styles.summaryTitle}>{`${previewed.label} leaves you`}</span>
                                <span>{`${describeLeftover(rows)}. ${previewed.afterNote}`}</span>
                            </>
                        ) : (
                            <>
                                <span className={styles.summaryTitle}>{`Not enough essence to ${previewed.label.toLowerCase()}`}</span>
                                <span>Finish a Crucible run to bank more.</span>
                            </>
                        )}
                    </div>
                )}
            </div>
            {item === null && <p className={styles.note}>Select an item to see what working on it costs.</p>}
        </Panel>
    );
}
